import React, { useMemo, useState, useEffect, useRef } from "react";
import { BrandCategory } from "@/screens/analysis/types";
import { RiCloseLine, RiSearchLine } from "@remixicon/react";
import { cn } from "@/lib/utils";

type CategoryChipProps = {
  category: BrandCategory;
  isSelected: boolean;
  onSelect: (key: string) => void;
  disabled?: boolean;
};

const CategoryChip: React.FC<CategoryChipProps> = ({
  category,
  isSelected,
  onSelect,
  disabled,
}) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(category.key)}
      disabled={disabled}
      className={cn(
        "group shrink-0 bg-white h-7 px-[10px] py-1 flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray text-[11px] text-brand-primary-800 leading-[19px] tracking-[0.02em] transition-colors",
        isSelected && "squircle-outline-color-brand-orange",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <span className="translate-y-[1px]">{category.label}</span>
    </button>
  );
};

type Props = {
  categories: BrandCategory[];
  selectedCategoryId: string | null;
  onSelect: (categoryId: string) => void;
  className?: string;
};

const CategoryChips: React.FC<Props> = ({
  categories,
  selectedCategoryId,
  onSelect,
  className,
}) => {
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  // Focus search input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((category) => category.label.toLowerCase().includes(term));
  }, [categories, searchTerm]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };
    update();
    const onScroll = () => update();
    el.addEventListener("scroll", onScroll, { passive: true } as AddEventListenerOptions);
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [filteredCategories.length, searchOpen]);

  return (
    <div className={`flex gap-[6px] ${className || ""}`}>
      {/* Search control */}
      {searchOpen ? (
        <div
          className="bg-white h-7 pl-2 pr-2 py-1 flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray"
          style={{ minWidth: 180 }}
        >
          <RiSearchLine size={14} className="text-brand-primary-400 shrink-0" />
          <input
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Category"
            className="outline-none border-0 ring-0 text-[11px] placeholder:text-[11px] leading-[18px] flex-1 text-brand-primary-800 placeholder:text-brand-primary-400 bg-transparent"
          />
          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setSearchOpen(false);
            }}
            className="p-0.5"
            aria-label="Clear search and close"
          >
            <RiCloseLine size={14} className="text-brand-primary-400" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          disabled={!categories || categories.length === 0}
          className={cn(
            "bg-white h-7 w-7 shrink-0 p-0 flex items-center justify-center rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray",
            (!categories || categories.length === 0) && "opacity-50 cursor-not-allowed",
          )}
          aria-label="Search categories"
        >
          <RiSearchLine size={14} className="text-[#9AA3BD]" />
        </button>
      )}

      <div className="relative overflow-hidden">
        <div
          ref={scrollRef}
          className="overflow-x-auto flex items-center gap-[6px] scrollbar-hide"
        >
          {filteredCategories.map((category) => {
            const isSelected = selectedCategoryId === category.key;
            return (
              <CategoryChip
                key={category.key}
                category={category}
                isSelected={isSelected}
                onSelect={onSelect}
              />
            );
          })}
        </div>
        {canScrollLeft && (
          <div
            className="absolute left-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
        {canScrollRight && (
          <div
            className="absolute right-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(270deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CategoryChips;
